import CalTax from '../../../utils/functions/CalTax';
import { sumCarryOut } from './table/cells';

// รวบรวมข้อมูล "วิธีคิดค่าแรง" ของทุกคนในงวด เพื่อแสดงใน PayrollFormulaDialog
// ไม่คำนวณสูตรใหม่ — ดึงตัวเลขจากแหล่งเดียวกับตาราง (buildDailyRowsForEmp / empSummaryMap / otBreakdownMap /
// faBreakdownMap / projectDashboard / fulltimeProjectSplit) แล้วจัดรูปให้อ่านเป็นขั้นตอน ตัวเลขจึงตรงกับตารางเสมอ

const num = (v) => parseFloat(v) || 0;
const sumBy = (arr, key) => (arr || []).reduce((s, x) => s + (Number(x?.[key]) || 0), 0);

const SSO_MIN = 83;
const SSO_MAX = 875;

export function buildPayrollExplain({
  timestamp,
  salaryMode,
  empSummaryMap,
  otBreakdownMap,
  faBreakdownMap,
  projectDashboard,
  fulltimeProjectSplit,
  fields,
  fieldIndexMap,
  watchedSalaryData,
  carryoverByEmpId,
  carryoverOutByEmpId,
  buildDailyRowsForEmp,
}) {
  const isWorkTime = salaryMode === 'work-time';
  const empRows = timestamp?.rows || [];

  const employees = empRows.map((emp) => {
    const empId = String(emp.employee_id);
    const isFulltime = emp.emp_type === 'FULLTIME';
    const spd = emp.salary_per_day || 0;
    const fi = fieldIndexMap?.[empId];
    const f = fi !== undefined ? fields?.[fi] : null;
    const rd = (fi !== undefined && watchedSalaryData?.[fi]) || {};
    const rev = (k) => num(rd?.revenue?.[k] ?? f?.revenue?.[k] ?? 0);
    const exp = (k) => num(rd?.expenses?.[k] ?? f?.expenses?.[k] ?? 0);
    const manualFields = f?.manual_fields || rd?.manual_fields || [];
    const summary = empSummaryMap?.[empId] || {};
    const ot = otBreakdownMap?.[empId] || { total: 0, rows: [] };
    const fa = faBreakdownMap?.[empId] || { total: 0, rows: [], faEnabled: false };

    // ── แถวรายวัน ──
    const allRows = buildDailyRowsForEmp(emp);
    const dayRows = allRows.filter((r) => !r.isEmpty);

    // รายเดือน: วันที่ไม่ได้ค่าแรง (ลาไม่ให้ค่าแรง / ติ๊กไม่ได้ค่าแรง) และวันที่ได้เรตพิเศษ
    // (หมายเหตุ type 'wage' ยังใช้กับ "แก้ค่าแรงโดย HR" ด้วย จึงกรองเฉพาะข้อความ "ไม่ได้ค่าแรง")
    const noWageText = (r) => (r.notes || [])
      .filter((n) => n.type === 'wage' && /ไม่ได้ค่าแรง/.test(n.text))
      .map((n) => n.text)
      .join(', ');
    const unpaidRows = isFulltime
      ? allRows.filter((r) => noWageText(r)).map((r) => ({ ...r, noWageText: noWageText(r) }))
      : [];
    const rateExtraRows = isFulltime
      ? allRows
        .filter((r) => {
          if (r.isEmpty || r.leave || r.isManual) return false;
          if (noWageText(r)) return false;
          const rate = r.parts?.ck && !r.parts.ck.isManualCk ? r.parts.ck.rate : 1;
          return rate > 1;
        })
        .map((r) => ({ ...r, extraRate: r.parts.ck.rate - 1 }))
      : [];

    // ── รายได้ ──
    const actualSalary = summary.actualSalaryAmt || 0;
    const displaySalary = summary.displayAmt || 0;
    const salaryBase = isWorkTime ? actualSalary : displaySalary;
    const dailySumActual = dayRows.reduce((s, r) => s + (isFulltime ? 0 : r.actualWage || 0), 0);
    const dailySumDisplay = dayRows.reduce((s, r) => s + (isFulltime ? 0 : r.wage || 0), 0);
    const incomes = {
      salary: actualSalary, // "ที่ต้องจ่าย" ใช้ค่าแรงตามชม.จริงเสมอ
      overtime: rev('overtime'),
      allowances: rev('allowances'),
      fieldAllowance: rev('field_allowance'),
      bonus: rev('bonus'),
    };
    // ยอดจากตารางรายวัน (ใช้เทียบกับยอดในฟอร์ม — ต่างกันเมื่อ HR แก้ตัวเลขเอง)
    const fromDaily = {
      overtime: ot.total || 0,
      allowances: dayRows.reduce((s, r) => s + (r.allowance || 0), 0),
      fieldAllowance: fa.total || 0,
    };

    // ── รายการหัก ──
    const ssoCfg = f?.sso_tax || {};
    const ssoEnabled = !!ssoCfg.status;
    const ssoRate = ssoCfg.number || 5;
    const ssoPaid = f?.sso_paid || 0;
    const ssoRaw = ssoEnabled ? salaryBase * (ssoRate / 100) : 0;
    let ssoCalc = ssoRaw;
    if (ssoEnabled && ssoCalc > 0) {
      if (ssoCalc + ssoPaid > SSO_MAX) ssoCalc = Math.max(0, SSO_MAX - ssoPaid);
      if (ssoCalc + ssoPaid < SSO_MIN) ssoCalc = Math.max(0, SSO_MIN - ssoPaid);
    }
    const taxCfg = f?.tax_type || null;
    const taxBase = (f?.salary_per_day || spd) * (f?.totalWork || 0);
    const taxAuto = CalTax(taxBase, taxCfg) || 0;
    const deductions = {
      tax: exp('tax'),
      sso: exp('sso'),
      crl: exp('crl'),
      other: exp('other'),
    };
    const carry = carryoverByEmpId?.[empId] || { addTotal: 0, deductTotal: 0, items: [] };
    const carryDelta = (Number(carry.addTotal) || 0) - (Number(carry.deductTotal) || 0);
    const carryOut = sumCarryOut(carryoverOutByEmpId?.[empId]);

    const payable = incomes.salary + incomes.overtime + incomes.allowances + incomes.fieldAllowance + incomes.bonus
      - deductions.tax - deductions.sso - deductions.crl - deductions.other + carryDelta;

    // ── การกระจายลงโครงการ (ดึงจาก projectDashboard ที่ตารางต้นทุนใช้จริง) ──
    const ftSplit = fulltimeProjectSplit?.[empId] || null;
    const projects = [];
    (projectDashboard || []).forEach((p) => {
      const myEmps = (p.employees || []).filter((e) => String(e.employee_id) === empId);
      const myDed = (p.employee_deductions || []).filter((d) => String(d.employee_id) === empId);
      const myPay = (p.employee_payables || []).filter((d) => String(d.employee_id) === empId);
      if (!myEmps.length && !myDed.length && !myPay.length) return;
      const key = p.project_id ? String(p.project_id) : 'no_project';
      const salary = sumBy(myEmps, 'amount');
      const otAmt = sumBy(myEmps, 'ot_amount');
      const faAmt = sumBy(myEmps, 'field_allowance_amount');
      const allowance = sumBy(myEmps, 'allowance');
      projects.push({
        key,
        number: p.project_number || '',
        name: p.project_name || '',
        // พนักงานที่ไม่จ่ายเงินตามโครงการ (แถว "ภายนอก" ของตารางต้นทุน)
        external: !!(myEmps[0]?.external || myPay[0]?.external || myDed[0]?.external),
        daysInProject: myEmps[0]?.days_in_project ?? null,
        salary,
        ot: otAmt,
        fa: faAmt,
        allowance,
        gross: salary + otAmt + faAmt + allowance,
        bonus: sumBy(myDed, 'bonus'),
        sso: sumBy(myDed, 'sso'),
        tax: sumBy(myDed, 'tax'),
        crl: sumBy(myDed, 'crl'),
        other: sumBy(myDed, 'other'),
        payable: sumBy(myPay, 'amount'),
        isDominant: (p.dominant_employee_ids || []).map(String).includes(empId),
        ft: ftSplit?.rows?.[key] || null,
      });
    });
    const grossSum = projects.reduce((s, p) => s + p.gross, 0);
    const remainder = isFulltime
      ? payable - grossSum
      : payable + deductions.other + deductions.crl - grossSum;
    // ส่วนต่างระหว่างยอดในตาราง (ที่ HR อาจแก้เอง) กับยอดรวมที่ลงโครงการจากรายวันตรงๆ
    const tableVsDaily = (incomes.salary + incomes.overtime + incomes.allowances + incomes.fieldAllowance) - grossSum;

    return {
      id: empId,
      name: emp.name || empId,
      empType: emp.emp_type,
      isFulltime,
      spd,
      hourRate: spd / 8,
      summary,
      salaryMode,
      isWorkTime,
      dayRows,
      unpaidRows,
      rateExtraRows,
      dailySumActual,
      dailySumDisplay,
      salaryBase,
      actualSalary,
      displaySalary,
      incomes,
      fromDaily,
      ot,
      fa,
      manualFields,
      sso: {
        enabled: ssoEnabled, rate: ssoRate, paid: ssoPaid, base: salaryBase, raw: ssoRaw, calc: ssoCalc,
      },
      tax: { cfg: taxCfg, base: taxBase, auto: taxAuto, days: f?.totalWork || 0 },
      deductions,
      carry,
      carryDelta,
      carryOut,
      payable,
      projects,
      grossSum,
      remainder,
      tableVsDaily,
      ftSplit,
    };
  });

  // ── ภาพรวมต่อโครงการ (ทุกคน) ──
  const nameById = {};
  employees.forEach((e) => { nameById[e.id] = e; });
  const projectsOverview = (projectDashboard || []).map((p) => {
    const empIds = new Set([
      ...(p.employees || []).map((e) => String(e.employee_id)),
      ...(p.employee_payables || []).map((e) => String(e.employee_id)),
      ...(p.employee_deductions || []).map((e) => String(e.employee_id)),
    ]);
    const list = [...empIds].map((id) => {
      const e = nameById[id];
      const mine = (p.employees || []).filter((x) => String(x.employee_id) === id);
      const ded = (p.employee_deductions || []).filter((x) => String(x.employee_id) === id);
      const pay = (p.employee_payables || []).filter((x) => String(x.employee_id) === id);
      return {
        id,
        name: e?.name || mine[0]?.name || id,
        empType: e?.empType || mine[0]?.emp_type,
        salary: sumBy(mine, 'amount'),
        ot: sumBy(mine, 'ot_amount'),
        fa: sumBy(mine, 'field_allowance_amount'),
        allowance: sumBy(mine, 'allowance'),
        bonus: sumBy(ded, 'bonus'),
        sso: sumBy(ded, 'sso'),
        tax: sumBy(ded, 'tax'),
        crl: sumBy(ded, 'crl'),
        other: sumBy(ded, 'other'),
        payable: sumBy(pay, 'amount'),
      };
    }).sort((a, b) => b.payable - a.payable);
    return {
      key: p.project_id ? String(p.project_id) : 'no_project',
      number: p.project_number || '',
      name: p.project_name || '',
      list,
      totalPayable: p.total_payable || 0,
      totalSalary: p.total_salary || 0,
      totalOt: p.total_ot || 0,
    };
  });

  return { employees, projectsOverview, salaryMode, isWorkTime };
}

